import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CTA, Icon, Modal, Text } from '../..';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import { Footer, ScrollableArea } from '../style';
import { MODAL_CLOSE } from '../../../../app/redux/actions';
import {
  COURSE_CREATE,
  GROUP_DELETE,
  QUIZ_DELETE,
  QUIZ_GROUP_DELETED,
  QUIZ_CREATION_RESET,
  COURSE_UPDATE,
} from '../../../../app/redux/actions/tutors';
import routes from '../../../../routes';
import theme from '../../../theme';
import { LoadingStep, ResultsWrapper } from './style';
import { hasPermission } from '../../../../utils/permissions';

export const ModalCreateCourseId = 'ModalCreateCourse';

const ModalCreateCourse = ({
  mediaIsPhone,
  isOpen,
  course,
  allQuiz,
  excludedNewGroupIds,
  createCourse,
  modalClose,
  courseCreation = {},
  deleteQuiz,
  deleteGroups,
  nextStep,
  quizCreationReset,
  updateCourse,
  permissions,
}) => {
  const { course_id } = useParams();
  const navigate = useNavigate();
  const { saving, loading, errors } = courseCreation;
  const scrollableAreaRef = useRef(null);
  const [payload, setPayload] = useState();
  const [courseConfirmed, setCourseConfirmed] = useState(false);

  useEffect(() => {
    if (!isOpen && courseConfirmed) {
      quizCreationReset();
      navigate(routes.tutorCourseList.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (course?.registration) {
      const { overview, activities, documents, registration, editMode } =
        course;
      const sections = Object.values(activities?.sections).map((s) => ({
        ...s,
        activities: Object.values(s?.activities || {}).map((a) => {
          const act = {
            type: a?.type,
          };
          // if node_id is still present activity has not been modified
          if (a?.data?.node_id) {
            act.activity_id = Number(a?.data?.id);
            act.nid = Number(a?.data?.node_id);
            // else pass only nid and create a new paragraph
          } else act.nid = Number(a?.data?.id);
          return act;
        }),
      }));

      //? if you edit the select it comes as "value", if preconfigured it comes as "node_id"
      const survey_nid = overview?.survey?.value || overview?.survey?.node_id;
      const survey = { nid: survey_nid, paragraph_id: overview?.survey?.id };

      const body = {
        // OVERVIEW TAB
        typology: 'course',
        title: overview?.title,
        expiration_date: overview?.expiration_date || undefined,
        hide_after_exp_date: overview?.hide_after_exp_date,
        region_id: Number(overview?.region) || undefined,
        province_id: Number(overview?.province) || undefined,
        description: overview?.description,
        catalog_tooltip: overview?.tooltip,
        catalog_id: overview?.catalog_id,
        category_id: Number(
          overview?.subject?.id ||
            overview?.category?.id ||
            overview?.subject?.value ||
            overview?.category?.value
        ),
        level_id: Number(overview?.level?.value) || undefined,
        published: overview?.published || false,
        allow_comments: overview?.allow_comments || false,
        tags: overview?.tags?.map((t) => t.id) || [],
        surveys: survey_nid ? [{ ...survey }] : [],
        course_cover: Number(overview?.course_cover || 0),
        course_panoramic_media: Number(overview?.panoramic_media || 0),
        related_courses: (overview?.related_courses || []).map((rc) => rc?.id),
        // ACTIVITIES TAB
        sections,
        sequential: activities?.sequential,
        // DOCUMENTS TAB
        documents_ids: documents || [],
        // REGISTRATION TAB
        mandatory_ffoo_ids: registration?.mandatory_ffoo_ids,
        included_groups_ids: registration?.included_groups,
      };
      if (editMode && !hasPermission(permissions, 'set_course_visibility')) {
        delete body.mandatory_ffoo_ids;
        delete body.included_groups_ids;
      }
      setPayload(body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  useEffect(() => {
    if (loading === 3) {
      const { activities, registration } = course;
      const quizList = [];
      Object.values(activities?.sections || {}).forEach((s) => {
        Object.values(s?.activities || {}).forEach((a) => {
          a?.type === 'activity_quiz' && quizList.push(a?.data?.id?.toString());
        });
      });
      const allQuizIds = Object.keys(allQuiz);
      const excludedQuiz = allQuizIds.filter((qId) => !quizList?.includes(qId));
      // remove unused quiz & groups
      deleteQuiz(excludedQuiz);

      const { included_groups } = registration || {};
      if (included_groups?.length > 0) {
        const excludedGroupIds = excludedNewGroupIds.filter(
          (gId) => !included_groups?.includes(gId)
        );
        deleteGroups(excludedGroupIds);
      }
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <Modal
      title={course_id ? 'Modifica percorso' : 'Crea percorso'}
      modalId={ModalCreateCourseId}
      scrollableAreaRef={scrollableAreaRef}
      forceOpen={saving}
    >
      <ScrollableArea
        ref={scrollableAreaRef}
        style={{ minWidth: 'min(60vw, 800px )', minHeight: 'min(200px, 30vh)' }}
      >
        {courseConfirmed ? (
          <ResultsWrapper>
            {saving ? (
              <Text type="h3" mb={8}>
                Per favore attendi che la creazione del percorso formativo sia
                completata
              </Text>
            ) : errors?.length > 0 ? (
              <>
                <Text type="h1.5" mb={8}>
                  Attenzione!
                </Text>
                <Text type="h3" mb={8}>
                  Qualcosa è andato storto durante la{' '}
                  {course_id ? 'modifica' : 'creazione'} del corso “
                  {course?.overview?.title}”
                </Text>
              </>
            ) : (
              <>
                <Text type="h1.5" mb={8}>
                  Complimenti!
                </Text>
                <Text type="h3" mb={8}>
                  Il percorso formativo “{course?.overview?.title}” è stato{' '}
                  {course_id ? 'modificato' : 'creato'} con successo
                </Text>
              </>
            )}
            <LoadingStep>
              <Text type="b4">
                {course_id ? 'Modifica' : 'Creazione'} Corso
              </Text>
              {loading > 2 ? (
                <Icon
                  type="loading-01"
                  className="rotating"
                  clickable={false}
                  size={24}
                />
              ) : errors?.includes('course') ? (
                <Icon
                  type="x-circle"
                  stroke={theme().colors.fail}
                  clickable={false}
                  size={24}
                />
              ) : (
                <Icon
                  type="check-circle"
                  stroke={theme().colors.successIcon}
                  clickable={false}
                  size={24}
                />
              )}
            </LoadingStep>
            <LoadingStep>
              <Text type="b4">Caricamento Immagini</Text>
              {loading > 1 ? (
                <Icon
                  type="loading-01"
                  className="rotating"
                  clickable={false}
                  size={24}
                />
              ) : errors?.includes('images') ? (
                <Icon
                  type="x-circle"
                  stroke={theme().colors.fail}
                  clickable={false}
                  size={24}
                />
              ) : (
                <Icon
                  type="check-circle"
                  stroke={theme().colors.successIcon}
                  clickable={false}
                  size={24}
                />
              )}
            </LoadingStep>
            <LoadingStep>
              <Text type="b4">Caricamento Documenti</Text>
              {loading > 0 ? (
                <Icon
                  type="loading-01"
                  className="rotating"
                  clickable={false}
                  size={24}
                />
              ) : errors?.includes('documents') ? (
                <Icon
                  type="x-circle"
                  stroke={theme().colors.fail}
                  clickable={false}
                  size={24}
                />
              ) : (
                <Icon
                  type="check-circle"
                  stroke={theme().colors.successIcon}
                  clickable={false}
                  size={24}
                />
              )}
            </LoadingStep>
          </ResultsWrapper>
        ) : (
          <>
            <Text type="h3" mb={8}>
              Stai {course_id ? 'modificando' : 'creando'} il percorso fomativo
              “{course?.overview?.title}”
            </Text>
            <Text type="b4">
              Cliccando su “conferma” procedi alla{' '}
              {course_id ? 'modifica' : 'creazione'} del percorso formativo
              indicato.
            </Text>
          </>
        )}
      </ScrollableArea>
      <Footer>
        {courseConfirmed ? (
          <>
            {errors?.length > 0 && (
              <CTA
                type="secondary"
                btnText="Riprova"
                fullsize={mediaIsPhone}
                onClick={() => window.location.reload()}
                disabled={saving}
              />
            )}
            <CTA
              btnText="Chiudi"
              fullsize={mediaIsPhone}
              onClick={() => {
                modalClose();
              }}
              disabled={saving}
            />
          </>
        ) : (
          <>
            <CTA
              type="secondary"
              btnText="annulla"
              fullsize={mediaIsPhone}
              onClick={modalClose}
              disabled={loading > 0}
            />
            <CTA
              type="primary"
              btnText="conferma"
              fullsize={mediaIsPhone}
              onClick={() => {
                if (course_id) updateCourse(course_id, payload);
                else createCourse(payload);
                setCourseConfirmed(true);
              }}
              disabled={loading > 0}
            />
          </>
        )}
      </Footer>
    </Modal>
  );
};

ModalCreateCourse.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  isOpen: PropTypes.bool,
  id: PropTypes.string,
  course: PropTypes.object,
  courseCreation: PropTypes.object,
  allQuiz: PropTypes.object,
  excludedNewGroupIds: PropTypes.array,
  permissions: PropTypes.array,
  // dispatch
  modalClose: PropTypes.func.isRequired,
  createCourse: PropTypes.func.isRequired,
  deleteQuiz: PropTypes.func.isRequired,
  deleteGroups: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  quizCreationReset: PropTypes.func.isRequired,
  updateCourse: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const {
        course,
        courseCreation,
        tempQuiz: { data: allQuiz },
        excludedNewGroupIds,
      } = state.tutors;
      const { isOpen } = state.app.modal;
      const { course: permissions } = state.permissions;
      return {
        isOpen,
        course,
        courseCreation,
        allQuiz,
        excludedNewGroupIds,
        permissions,
      };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
      createCourse: (payload) =>
        dispatch({ type: COURSE_CREATE._REQUEST, payload }),
      deleteQuiz: (ids) => dispatch({ type: QUIZ_DELETE._REQUEST, ids }),
      deleteGroups: (ids) => dispatch({ type: GROUP_DELETE._REQUEST, ids }),
      nextStep: () => dispatch({ type: QUIZ_GROUP_DELETED, loading: 2 }),
      quizCreationReset: () => dispatch({ type: QUIZ_CREATION_RESET }),
      updateCourse: (id, payload) =>
        dispatch({ type: COURSE_UPDATE._REQUEST, id, payload }),
    })
  ),
  withMediaQueries
)(ModalCreateCourse);
